/* =====================================================================
 * AdminUsageChart — tenant-wide tokens or runs per bucket.
 *
 * The bucket (일 / 주 / 월) is not chosen by the user: the page derives it
 * from the window being shown (최근 7·30·90·365일·전체 or a custom range),
 * see `adminDashboardRange.ts`. This component only formats whatever
 * `data.granularity` the server echoes back.
 *
 * Form: single-series bars. Magnitude over discrete time buckets is bars,
 * and the question here is "how much in total", so there is one series.
 *
 * Deliberately **not** stacked by 부서. The validated palette holds two
 * categorical slots (`chartPalette.ts`, "do not eyeball a replacement"),
 * while a tenant has dozens of departments — cycling two hues would make
 * colour meaningless and identity would rest on nothing. The per-department
 * split is answered by the ranked breakdown below the chart, where identity
 * is carried by its label. Selecting one 본부 or 부서 filters this chart
 * instead of adding a series to it.
 *
 * One axis. Never two.
 * ===================================================================== */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type {
  AdminDashboardGranularity,
  AdminDashboardSeries,
} from '@auw/api-client';

import { CATEGORICAL, type ChartMode } from '../dashboard/chartPalette';
import {
  formatDashboardTokenCount,
  type UsageMetric,
  formatUsageTick,
} from '../dashboard/dashboardMetrics';
import { EmptyState, ScrollX } from '../dashboard/dashboard.styles';

const PLOT_H = 168;
const BAR_GAP = 8;
/* Column width per bucket. A month or year tick ("25년 10월", "Oct 25") is
 * about twice as wide as a day tick ("9. 7."); at the day width the month
 * labels of 최근 365일 / 전체 overlap into an unreadable strip. The bar fills
 * its column, so month bars are wider too — every bar in one chart still has
 * the same width, which is what keeps the height comparison honest. */
const COLUMN_W_BY_GRANULARITY: Record<AdminDashboardGranularity, number> = {
  day: 22,
  week: 22,
  month: 48,
  year: 48,
};
/* Tick labels are wider than a bar and overhang it on both sides; without
 * this the first one is clipped by the scroller's edge. */
const TICK_OVERHANG = 18;

const Plot = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${BAR_GAP}px;
  height: ${PLOT_H}px;
  padding: 20px ${TICK_OVERHANG}px 0;
  position: relative;
`;

const Baseline = styled.div`
  position: absolute;
  left: ${TICK_OVERHANG}px;
  right: ${TICK_OVERHANG}px;
  bottom: 0;
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

const Column = styled.div<{ $w: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: ${({ $w }) => $w}px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const Stack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: ${PLOT_H}px;
`;

const Bar = styled.div<{ $color: string; $h: number }>`
  width: 100%;
  height: ${({ $h }) => Math.max($h, 3)}px;
  background: ${({ $color }) => $color};
  border-radius: 5px;
`;

const Value = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

const Tick = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

export interface AdminUsageChartProps {
  data: AdminDashboardSeries;
  metric: UsageMetric;
  mode: ChartMode;
}

export const AdminUsageChart: React.FC<AdminUsageChartProps> = ({
  data,
  metric,
  mode,
}) => {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  const valueOf = (point: AdminDashboardSeries['points'][number]) =>
    metric === 'tokens' ? point.total_token_count : point.total_runs;

  const max = useMemo(
    () => Math.max(1, ...data.points.map(valueOf)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.points, metric],
  );

  /* Buckets are timezone-less calendar labels: the server names each one by
   * its own first KST day, so they are formatted as UTC to keep the browser's
   * zone from shifting the label off the day it stands for. */
  const yearFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { year: 'numeric', timeZone: 'UTC' }),
    [i18n.language],
  );
  /* 일·주·월 눈금은 개인 대시보드와 같은 규칙(일·주 "8/20")을 쓴다. */
  const formatTick = (bucket: string) =>
    data.granularity === 'year'
      ? yearFormatter.format(new Date(`${bucket}T00:00:00Z`))
      : formatUsageTick(bucket, data.granularity, i18n.language);

  if (!data.points.length) {
    return <EmptyState>{t('common:adminDashboard.noUsageYet')}</EmptyState>;
  }

  const color = CATEGORICAL[mode][0];
  const columnWidth =
    COLUMN_W_BY_GRANULARITY[data.granularity] ?? COLUMN_W_BY_GRANULARITY.day;

  return (
    <ScrollX>
      <Plot>
        <Baseline aria-hidden />
        {data.points.map((point) => {
          const value = valueOf(point);
          const height = (value / max) * (PLOT_H - 24);
          const label = `${formatTick(point.bucket)} · ${t(
            metric === 'tokens'
              ? 'common:dashboard.tokensCount'
              : 'common:dashboard.runsCount',
            { count: new Intl.NumberFormat(i18n.language).format(value) },
          )}`;
          return (
            <Column
              key={point.bucket}
              $w={columnWidth}
              role="img"
              tabIndex={0}
              aria-label={label}
              title={label}
              onMouseEnter={() => setHovered(point.bucket)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(point.bucket)}
              onBlur={() => setHovered(null)}
            >
              <Stack>
                <Bar $color={color} $h={height} />
              </Stack>
              {/* Direct label only on the hovered column — a number on every
                  bar turns the plot into a table. */}
              <Value>
                {hovered === point.bucket
                  ? metric === 'tokens'
                    ? formatDashboardTokenCount(value)
                    : value
                  : ''}
              </Value>
              <Tick>
                {formatTick(point.bucket)}
              </Tick>
            </Column>
          );
        })}
      </Plot>
    </ScrollX>
  );
};
